import { motion, useReducedMotion } from 'framer-motion';
import { TIER_META, type TierName } from '@/features/agent-progress/tierConstants';
import { useT } from '@/i18n';

type Size = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<Size, { container: string; icon: string; text: string }> = {
  sm: { container: 'px-2 py-0.5',   icon: 'w-3.5 h-3.5', text: 'text-xs' },
  md: { container: 'px-2.5 py-1',   icon: 'w-4 h-4',     text: 'text-sm' },
  lg: { container: 'px-3.5 py-1.5', icon: 'w-5 h-5',     text: 'text-base' },
};

export interface TierBadgeProps {
  tier: TierName;
  size?: Size;
  /** Show only the icon (no label text) */
  iconOnly?: boolean;
  /** Disable entrance animation (used inside lists) */
  noAnimate?: boolean;
}

export function TierBadge({ tier, size = 'md', iconOnly = false, noAnimate = false }: TierBadgeProps) {
  const meta = TIER_META[tier];
  const reduce = useReducedMotion();
  const t = useT();
  const Icon = meta.icon;
  const sz = SIZE_CLASSES[size];

  const content = (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${meta.bgClass} ${meta.textClass} ring-1 ${meta.ringClass} ${sz.container} ${sz.text}`}
    >
      <Icon className={sz.icon} aria-hidden />
      {!iconOnly && <span>{t(meta.i18nLabelKey as never)}</span>}
    </span>
  );

  if (noAnimate || reduce) return content;

  return (
    <motion.span
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 18 }}
      className="inline-block"
    >
      {content}
    </motion.span>
  );
}
